/**
 * ProfileAvatar — 92px circular avatar with a blue edit badge.
 * Web port of client-native ProfileAvatarWithProgress (showLevelBadge=false on
 * the live profile, so no level pill is rendered here).
 */
import { FC } from "react";
import { Pencil } from "lucide-react";
import { colors } from "@/ui";

const AVATAR_SIZE = 92;

interface ProfileAvatarProps {
  src?: string;
  initials?: string;
  onPress?: () => void;
}

const ProfileAvatar: FC<ProfileAvatarProps> = ({ src, initials, onPress }) => (
  <button
    type="button"
    onClick={onPress}
    aria-label="Change profile picture"
    style={{
      position: "relative",
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      padding: 0,
      border: "none",
      background: "none",
      cursor: "pointer",
    }}
  >
    <div
      style={{
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        borderRadius: AVATAR_SIZE / 2,
        // Plain light-gray circle, matching iOS. The bird PNGs ship with their
        // own light-gray disc baked in, so on iOS the orange fill is never
        // visible; on the web the bird's transparent corners exposed the fill
        // as an orange RING. Backing the avatar with light gray removes the
        // ring while keeping a neutral disc behind initials.
        backgroundColor: colors.greyLight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {src ? (
        <img
          src={src}
          alt="Profile"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <span className="text-3xl font-bold" style={{ color: colors.primary }}>
          {initials ?? "?"}
        </span>
      )}
    </div>
    <div
      style={{
        position: "absolute",
        bottom: -8,
        right: -4,
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: colors.blue,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: `2px solid ${colors.background}`,
      }}
    >
      <Pencil size={14} color={colors.white} />
    </div>
  </button>
);

export default ProfileAvatar;
